import React, { useState, useEffect } from 'react';
import { motion, useScroll } from 'framer-motion';
import { ChevronUp } from 'lucide-react';

// Import components
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Import sections
import HeroSection from '../components/sections/HeroSection';
import CallingSection from '../components/sections/CallingSection';
import JourneySection from '../components/sections/JourneySection';
import ImpactSection from '../components/sections/ImpactSection';
import MinistryArmsSection from '../components/sections/MinistryArmsSection';
import PhotoAlbumSection from '../components/sections/PhotoAlbumSection';
import PartnershipSection from '../components/sections/PartnershipSection';
import WifeJourneySection from '../components/sections/WifeJourneySection';
import TestimonialsSection from '../components/sections/TestimonialsSection';
import BooksSection from '../components/sections/BooksSection';
import ConnectSection from '../components/sections/ConnectSection';

const LandingPage = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const { scrollYProgress } = useScroll();
  
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const toggleTheme = () => {
    setDarkMode(!darkMode);
  };

  return (
    <div className={`${darkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'} overflow-hidden font-sans transition-colors duration-300`}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
        * {
          font-family: 'Space Grotesk', sans-serif;
        }
      `}</style>

      {/* Progress Bar */}
      <motion.div 
        className={`fixed top-0 left-0 right-0 h-1 ${darkMode ? 'bg-white' : 'bg-slate-900'} origin-left z-50`}
        style={{ scaleX: scrollYProgress }}
      />

      {/* Navbar */}
      <Navbar 
        scrollToSection={scrollToSection} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
        darkMode={darkMode}
        toggleTheme={toggleTheme}
      />

      {/* Hero Section */}
      <section id="home">
        <HeroSection scrollToSection={scrollToSection} darkMode={darkMode} />
      </section>

      {/* The Calling */}
      <section id="calling">
        <CallingSection darkMode={darkMode} />
      </section>

      {/* The Journey */}
      <section id="journey">
        <JourneySection darkMode={darkMode} />
      </section>

      {/* Impact Numbers */}
      <section id="impact">
        <ImpactSection darkMode={darkMode} />
      </section>

      {/* Ministry Arms */}
      <section id="ministry">
        <MinistryArmsSection darkMode={darkMode} />
      </section>

      {/* Photo Album */}
      <section id="album">
        <PhotoAlbumSection darkMode={darkMode} />
      </section>

      {/* Partnership Section */}
      <section id="partnership">
        <PartnershipSection darkMode={darkMode} />
      </section>

      {/* Pastor's Wife Journey */}
      <section id="wife-journey">
        <WifeJourneySection darkMode={darkMode} />
      </section>

      {/* Testimonials */}
      <section id="testimonials">
        <TestimonialsSection darkMode={darkMode} />
      </section>

      {/* Books */}
      <section id="books">
        <BooksSection darkMode={darkMode} />
      </section>

      {/* Connect */}
      <section id="connect">
        <ConnectSection darkMode={darkMode} />
      </section>

      {/* Footer */}
      <Footer scrollToSection={scrollToSection} darkMode={darkMode} />

      {/* Back to Top */}
      {showBackToTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          onClick={scrollToTop}
          className={`fixed bottom-8 right-8 ${darkMode ? 'bg-white text-slate-900 hover:bg-slate-100' : 'bg-slate-900 text-white hover:bg-slate-800'} p-4 rounded-xl shadow-lg z-40 transition-colors flex items-center justify-center`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <ChevronUp className="w-6 h-6" />
        </motion.button>
      )}
    </div>
  );
};

export default LandingPage;